/**
 * Navigable catalog tree for the drill-down browser. The catalog loader already
 * produces a resolved tree (`ResolvedCatalog` with packs + subcatalogs); this keeps
 * that structure (instead of flattening it) and adds stable `path`s so a screen can
 * push into a node and find it again. Pure, so it's unit-tested.
 */
import { CatalogFacet, CatalogPackEntry, CountLabels } from "../core/catalog";
import { NodeRelatedRef, resolvePlatforms, resolveVisibility } from "../core/manifest";
import { CatalogTerms } from "../legal/catalogTerms";
import { normalizeCatalogUrl } from "../permalink/permalink";
import { ResolvedCatalog } from "./catalogLoader";

/** How to remove/block a user-added root catalog — see `CatalogNode.removableSource`. */
export type RemovableCatalogSource = { kind: "url"; url: string } | { kind: "inline"; id: string };

export interface CatalogNode {
  id: string;
  title: string;
  /** Slash-joined id path from the virtual root (e.g. "ministry-training"). */
  path: string;
  packs: CatalogPackEntry[];
  children: CatalogNode[];
  /** Git-derived rolled-up newest change date (ISO YYYY-MM-DD), for an "Updated" hint. */
  updated?: string;
  /**
   * Globally unique per-appearance id (materials plan 022), mirrored from the referencing
   * `ResolvedCatalog.placementId` — distinct from the canonical `id`. Lets a stored
   * reference (`SavedCatalog`) pin an exact appearance of a cross-listed catalog. See
   * docs/055-STORED-REFS-CANONICAL-IDS.md.
   */
  placementId?: string;
  /**
   * Plain-text blurb about this node, carried from the resolved catalog's own top-level
   * `description` (docs/047-CONTENT-DESCRIPTIONS.md) — no cross-level resolution, so the
   * synthetic virtual root (built from multiple roots, no single source document) never
   * has one.
   */
  description?: string;
  /**
   * Filter axes mirrored from `ResolvedCatalog.facets` (this node's own catalog document —
   * docs/195-CATALOG-ENTRY-FILTER-CHIPS.md v0). v0 reads this only as the auto Language
   * chip row's **opt-out** signal (`facets: []` suppresses it); rendering chip groups from
   * declared facets is v1.
   */
  facets?: CatalogFacet[];
  /**
   * Overridable count-line nouns, mirrored from `ResolvedCatalog.countLabels`
   * (docs/260-OVERRIDABLE-COUNT-LABEL-NOUNS.md v0) — read at the render site
   * (`src/ui/CatalogBrowser.tsx`) by `content/countLabels.ts` `formatCountLabel`.
   */
  countLabels?: CountLabels;
  /**
   * This catalog document's own terms-of-usage, mirrored from `ResolvedCatalog.terms`
   * (docs/297-PROVIDER-CATALOG-TERMS-ACCEPTANCE.md v0) — same node's-own-document mirror
   * point as `description`/`countLabels`. Read at the open-gate call sites
   * (`app/(tabs)/catalog.tsx`/`app/catalog-node.tsx`) via `catalogTermsScope`/
   * `termsToAccept` (`legal/catalogTerms.ts`), and shown as a "View terms" link in the
   * Details sheet when present.
   */
  terms?: CatalogTerms;
  /**
   * Node-level "Related courses" see-also, mirrored from `ResolvedCatalog.related`
   * (docs/242-RELATED-AND-SAMEAS-RENDERING.md v0), resolved for display by
   * `content/nodeRelated.ts`.
   */
  related?: NodeRelatedRef[];
  /**
   * Opaque cross-placement equality key, mirrored from `ResolvedCatalog.sameAs`
   * (docs/242-RELATED-AND-SAMEAS-RENDERING.md v0), grouped by `content/sameAs.ts`
   * `sameAsGroups`.
   */
  sameAs?: string;
  /**
   * Free-form author-declared section label, mirrored from `ResolvedCatalog.group`
   * (docs/249-CATALOG-GROUPING-AND-SECTIONS.md v0) — bucketed verbatim by
   * `content/catalogGrouping.ts` `groupEntries(by: "group")`.
   */
  group?: string;
  /**
   * Author-declared course level, mirrored from `ResolvedCatalog.level` (docs/249-
   * CATALOG-GROUPING-AND-SECTIONS.md v0) — also the auto-default group-by signal
   * (`resolveDefaultGroupBy`).
   */
  level?: number;
  /**
   * Mirrored from `ResolvedCatalog.providerId` (docs/276-GROUP-BY-PROVIDERS.md v0) —
   * app-side-only, present only on a signed-in provider's aggregating root node, never
   * a descendant. Drives the "Group by: Providers" axis (`content/catalogGrouping.ts`
   * `groupByProvider`).
   */
  providerId?: string;
  /** Mirrored from `ResolvedCatalog.providerName` — the "Group by: Providers" section
   * title, used verbatim (never an i18n key). Same root-only scope as `providerId`. */
  providerName?: string;
  /**
   * Mirrored from `ResolvedCatalog.removableSource` — present only on a **root** node the
   * user added themselves (URL or inline/imported). Drives the Catalogs-page long-press
   * "Remove"/"Block" actions; absent for the bundled sample, curated preset catalogs, and
   * provider catalogs, and never appears below the root level (a sub-catalog's own
   * `ResolvedCatalog` never sets it).
   */
  removableSource?: RemovableCatalogSource;
  /**
   * Mirrored from `ResolvedCatalog.originUrl` — the URL this **root** was loaded from, when
   * it has one (preset/starter, user-added, or a single-catalog provider root). See
   * `ResolvedCatalog.originUrl`'s own doc comment; used only by {@link findNodeByCatalogUrl}.
   */
  originUrl?: string;
  /**
   * Mirrored from `ResolvedCatalog.entryUrl` — the referencing entry's own URL, present for
   * ANY URL-fetched catalog node (root or sub-catalog), unlike `originUrl` above (root-only).
   * See `ResolvedCatalog.entryUrl`'s own doc comment; the one consumer today is the admin
   * app's grant-`ref` derivation (`content/grantRef.ts`).
   */
  entryUrl?: string;
  /**
   * Set when this node's own catalog document failed to load (mirrored from
   * `ResolvedCatalog.loadError`) — a placeholder for an un-fetchable catalog, carrying no
   * packs/children. The browse row shows a red "Error" badge; the node's screen shows the
   * message. See `ResolvedCatalog.loadError`.
   */
  loadError?: string;
  errors: string[];
}

/**
 * Browse filtering: `hidden` pack entries/child catalogs are dropped from the tree
 * (docs/038-CONTENT-CAPABILITY-FLAGS.md §3). `private` has no dedicated "private section"
 * UI yet, so this v0 treats it the same as `hidden` — both are excluded from browse.
 * That's a deliberate (documented) decision, not an oversight; revisit if/when a
 * private-section UI is built.
 *
 * Also drops an item opted out of the `current` platform (docs/207-PLATFORM-AVAILABILITY-
 * FILTER.md) via `resolvePlatforms` — `current` omitted (`undefined`) means no platform
 * filtering at all, preserving today's behavior for a caller that doesn't know/care about
 * the running platform (e.g. `apps/admin`, which browses everything).
 */
function isBrowsable(
  visibility: ResolvedCatalog["visibility"] | CatalogPackEntry["visibility"],
  platforms: ResolvedCatalog["platforms"] | CatalogPackEntry["platforms"],
  current: string | undefined,
  includeHidden: boolean,
): boolean {
  // `apps/admin` grants access to catalogs (incl. `visibility:"private"`), so it must
  // SEE them — `includeHidden` bypasses both the visibility and platform filters. Every
  // consumer call site defaults it to `false` (unchanged browse behavior).
  if (includeHidden) return true;
  if (resolveVisibility(visibility) !== "public") return false;
  if (current !== undefined && !resolvePlatforms(current, platforms)) return false;
  return true;
}

function toNode(
  rc: ResolvedCatalog,
  parentPath: string,
  current: string | undefined,
  includeHidden: boolean,
): CatalogNode {
  const path = parentPath ? `${parentPath}/${rc.id}` : rc.id;
  return {
    id: rc.id,
    title: rc.title,
    path,
    packs: rc.packs.filter((p) => isBrowsable(p.visibility, p.platforms, current, includeHidden)),
    children: rc.subcatalogs
      .filter((s) => isBrowsable(s.visibility, s.platforms, current, includeHidden))
      .map((s) => toNode(s, path, current, includeHidden)),
    updated: rc.updated,
    placementId: rc.placementId,
    description: rc.description,
    facets: rc.facets,
    countLabels: rc.countLabels,
    terms: rc.terms,
    related: rc.related,
    sameAs: rc.sameAs,
    group: rc.group,
    level: rc.level,
    providerId: rc.providerId,
    providerName: rc.providerName,
    removableSource: rc.removableSource,
    originUrl: rc.originUrl,
    entryUrl: rc.entryUrl,
    loadError: rc.loadError,
    errors: rc.errors,
  };
}

/**
 * Build one navigable tree from the loaded catalog roots. Each root is kept as a
 * top-level group node — its title is the provider/source name (e.g. "Adan's
 * Teaching Catalog") — so catalogs from different people/ministries stay grouped.
 * Tapping a group drills into its corpora.
 *
 * `current` (docs/207-PLATFORM-AVAILABILITY-FILTER.md, e.g. `currentPlatform()`) is the
 * running platform's token, used to prune pack entries/child catalogs opted out via
 * `platforms`; omitted ⇒ no platform filtering (every existing call site keeps its
 * current behavior until it opts in by passing one).
 */
export function buildCatalogTree(
  roots: ResolvedCatalog[],
  current?: string,
  includeHidden = false,
): CatalogNode {
  return {
    id: "",
    title: "",
    path: "",
    packs: [],
    children: roots.map((r) => toNode(r, "", current, includeHidden)),
    errors: roots.flatMap((r) => r.errors),
  };
}

/**
 * A node's `id` is eligible to be deduplicated by `dedupTreeById` — excludes the
 * synthetic virtual-root id (`""`), a failed-load placeholder id (`catalogErrorNodeId`'s
 * `error:`-prefixed slug — the same guard `dedupRootsById` in `rootLoadBatch.ts` uses, so
 * a transient outage never collapses/splits an otherwise-legitimate row), and a provider's
 * own aggregating root id (`provider:<id>`, always unique per signed-in provider, never a
 * genuine duplicate — see `ResolvedCatalog.providerId`'s doc comment). A provider root's
 * CHILDREN dedup normally; only the wrapper node itself is excluded.
 */
function dedupEligible(id: string): boolean {
  return !!id && !id.startsWith("error:") && !id.startsWith("provider:");
}

/**
 * Is `node` itself, or a `provider:<id>` wrapper root, provider-sourced? True for the
 * docs/276-GROUP-BY-PROVIDERS.md `provider:<id>` aggregating wrapper (id-prefix) AND a
 * docs/289-FLATTEN-PROVIDER-CATALOGS-TO-ROOT.md flattened root (which carries
 * `providerId` directly, no wrapper) — the two shapes a provider grant can take at the
 * point it enters the tree. A plain descendant (a nested granted catalog under either
 * shape) carries neither signal itself — see {@link dedupTreeById}'s BFS, which
 * propagates provider-ness down from whichever ancestor first matched this.
 */
function isProviderSourced(node: CatalogNode): boolean {
  return node.id.startsWith("provider:") || node.providerId != null;
}

/**
 * Is `node` a **user-URL-added** root — `removableSource.kind === "url"`? Scoped
 * narrowly (docs/291-PROVIDER-PREFERRED-OVER-MANUAL-CATALOGS.md v0's own scope
 * decision, resolving the plan's Open Question 1 to the owner's literal wording,
 * "manual catalogs from URL") — deliberately excludes an inline/imported catalog
 * (`removableSource.kind === "inline"`) and a curated preset/starter catalog (no
 * `removableSource` at all). Only a ROOT ever carries `removableSource` (never a
 * descendant, see `CatalogNode.removableSource`'s own doc comment), so this is never
 * true for a nested node itself — {@link dedupTreeById}'s BFS propagates the signal
 * down from whichever ancestor root first matched this, mirroring how provider-ness
 * propagates from a `provider:<id>` wrapper.
 */
function isManualUrlSourced(node: CatalogNode): boolean {
  return node.removableSource?.kind === "url";
}

/**
 * Collapse a catalog `id` that's reachable via **more than one tree placement** down to
 * a single kept occurrence, pruning every other duplicate subtree — so a catalog
 * referenced both directly (e.g. a provider's own top-level grant) and nested inside
 * another catalog's umbrella (e.g. that umbrella lists it as a member) shows only once.
 * See docs/281-WHOLE-TREE-DEDUP-AND-INDEX-GRANT.md §A for the original shallowest-wins
 * rationale; the short version:
 *
 * - **Shallowest wins, ties keep configured/traversal order** — a BFS over `root`'s
 *   descendants records the FIRST node seen per eligible `id` as that id's "keeper" (by
 *   object identity); since BFS visits shallower levels before deeper ones, and siblings
 *   in traversal order otherwise, the keeper is always the shallowest occurrence, and a
 *   same-depth tie is broken by which sibling comes first.
 * - **An `id` occurring only once is never touched** — dedup only removes an occurrence
 *   when *another* occurrence of the same id already has a keeper recorded, so a catalog
 *   granted as a root but never nested anywhere else always still shows.
 * - **Catalog nodes only, never packs** — a pack cross-listed under two different catalogs
 *   is intentional curated composition (its completion already collapses across
 *   placements by `packId`, docs/170), so only `children` (catalog subtrees) are pruned;
 *   each kept node's own `packs` array is left exactly as `buildCatalogTree` produced it.
 * - **Safe for management affordances** — `removableSource`/`originUrl`/the update
 *   baseline exist only on a depth-1 root (never below), so keeping the shallowest
 *   occurrence can never strand a Remove/Block button behind a pruned deeper duplicate.
 *
 * **docs/291-PROVIDER-PREFERRED-OVER-MANUAL-CATALOGS.md v0 layers a NARROW
 * source-priority tie-break ON TOP of shallowest-wins:** when the same `id` appears as
 * both a **provider-sourced** occurrence (a `provider:<id>` wrapper's descendant, OR a
 * docs/289 flattened provider root and its descendants — {@link isProviderSourced}) and
 * a **user-URL-added** occurrence ({@link isManualUrlSourced} — a `removableSource.kind
 * === "url"` root or its descendants; scoped to the owner's literal ask, "manual
 * catalogs from URL" — NOT inline/imported catalogs, NOT curated preset/starter
 * catalogs, both of which stay a plain rank TIE with a provider, unaffected), the
 * **provider occurrence wins regardless of depth**. Both signals propagate down through
 * the BFS since only the entry-point root itself carries either directly. On a rank TIE
 * — provider-vs-provider, non-manual-URL-vs-non-manual-URL, OR a provider-vs-anything-
 * that-isn't-a-manual-URL-root (curated preset/starter, inline/imported, bundled) —
 * the behavior is byte-for-byte unchanged: shallowest wins, first-in-order on a
 * same-depth tie. This is why docs/281's own test suite is the regression gate for this
 * change and passes unmodified. Pruning a manual URL root here never strands its
 * Remove/Block affordance — that lives in `CatalogStore` by URL, not on the tree node.
 *
 * Pure and idempotent: `dedupTreeById(dedupTreeById(t))` structurally equals
 * `dedupTreeById(t)`, since every kept node is already each eligible id's unique/shallowest
 * (highest-rank-first) occurrence on the second pass.
 */
export function dedupTreeById(root: CatalogNode): CatalogNode {
  const keeper = primaryKeepers(root);
  const rebuild = (node: CatalogNode): CatalogNode => ({
    ...node,
    children: node.children
      // Prune a child only when its id HAS a primary keeper elsewhere and this isn't it.
      // An id with no primary occurrence (`!keeper.has`) is a pure nested cross-listing —
      // every placement is kept.
      .filter(
        (child) =>
          !dedupEligible(child.id) ||
          !keeper.has(child.id) ||
          keeper.get(child.id)!.node === child,
      )
      .map(rebuild),
  });
  return rebuild(root);
}

/** A single dedup-eligible primary occurrence's recorded "keeper" — see {@link primaryKeepers}. */
interface PrimaryKeeper {
  node: CatalogNode;
  provider: boolean;
  manualUrl: boolean;
}

/**
 * The shared BFS behind {@link dedupTreeById} (the prune pass) AND
 * {@link dedupDisableReason} (the "why was this row disabled" lookup, docs/313-PROVIDER-
 * CATALOG-LONGPRESS-AND-DEDUP-REASON.md v0) — walks `root`'s descendants and records, per
 * dedup-eligible `id`, the ONE **primary** occurrence `dedupTreeById` would keep for it.
 *
 * Only a **primary** occurrence — a top-level root, or a **grant** (a direct child of a
 * `provider:<id>` aggregating wrapper) — competes for the single keeper slot per id. A
 * catalog referenced purely **nested** (a `url` sub-catalog deep inside a curriculum —
 * e.g. the same course cross-listed under both a "Tracks" and a "By course" view with
 * DISTINCT `placementId`s, docs/055/242) has NO primary occurrence, so it is never
 * deduped and every placement renders. This keeps docs/281's collapse (a catalog that is
 * BOTH its own root/grant AND nested → keep the root) and docs/291 (a provider grant
 * beats a manual-URL root among primaries), while no longer emptying an intentionally
 * cross-listed sub-catalog — the regression that made a whole "Tracks" curriculum show
 * "0 packs" because its courses were also listed under "By course" (docs/281 fix).
 *
 * Deliberately NOT a blind `findNodeById` walk (which would return whichever occurrence —
 * primary OR a legitimately-kept nested cross-listing — happens to sit first in traversal
 * order): a nested cross-listing that shares an id with its own primary root is real,
 * intentional, simultaneously-kept content (see the class above), so treating it as "the
 * dedup outcome" would misreport a perfectly-alive primary root as pruned. Restricting the
 * walk to PRIMARY occurrences only is what makes `dedupDisableReason` safe.
 */
function primaryKeepers(root: CatalogNode): Map<string, PrimaryKeeper> {
  const keeper = new Map<string, PrimaryKeeper>();
  // BFS = shallowest level first; each queued entry carries whether it (or an ancestor
  // already in this walk) is provider-sourced / manual-URL-sourced, plus whether it is a
  // primary occurrence. A node's children are primary only when the node is a
  // `provider:<id>` wrapper (its children are grants — first-class listings); every deeper
  // level is nested. Top-level roots (the initial queue) are primary by construction.
  type Queued = { node: CatalogNode; provider: boolean; manualUrl: boolean; primary: boolean };
  const queue: Queued[] = root.children.map((c) => ({
    node: c,
    provider: isProviderSourced(c),
    manualUrl: isManualUrlSourced(c),
    primary: true,
  }));
  while (queue.length > 0) {
    const { node, provider, manualUrl, primary } = queue.shift()!;
    if (primary && dedupEligible(node.id)) {
      const cur = keeper.get(node.id);
      // Replace the keeper ONLY when the new occurrence is provider-sourced AND the
      // CURRENT keeper is specifically manual-URL-sourced — narrower than "any
      // strictly-higher rank," so a provider never displaces a curated preset/starter
      // or an inline/imported keeper, only a manual URL one (docs/291 v0's own scope).
      if (!cur || (provider && !cur.provider && cur.manualUrl)) {
        keeper.set(node.id, { node, provider, manualUrl });
      }
    }
    // Grants (the children of a `provider:<id>` wrapper) are the only non-top-level nodes
    // that count as primary; everything deeper is a nested cross-listing.
    const childrenPrimary = node.id.startsWith("provider:");
    for (const child of node.children) {
      queue.push({
        node: child,
        provider: provider || isProviderSourced(child),
        manualUrl: manualUrl || isManualUrlSourced(child),
        primary: childrenPrimary,
      });
    }
  }
  return keeper;
}

/**
 * `id → providerName` for every catalog node that is provider-sourced (see
 * {@link isProviderSourced}) — a `provider:<id>` wrapper root's descendants (docs/276;
 * the wrapper's own id is never itself a real catalog id, so it's excluded by
 * `dedupEligible`) or a docs/289 flattened provider root and ITS descendants. Reuses
 * the exact same provider-ancestry propagation `dedupTreeById`'s keeper uses — the
 * provider NAME is only ever recorded on the entry-point node itself (the wrapper or
 * the flattened root's own `providerName`), so it's carried down unchanged through
 * every descendant once a walk has entered that provider's subtree.
 *
 * Feeds Manage Catalogs' "Also provided by {name}" flag (docs/291-PROVIDER-PREFERRED-
 * OVER-MANUAL-CATALOGS.md §Crux #2) — a manually-added catalog whose resolved `id` is a
 * key of this map is redundant with a provider grant of the same content (Crux #1
 * already prunes its tree node in favor of the provider's copy; this map is what a
 * screen with no tree node to inspect — a `CatalogStore` URL row — checks instead). The
 * FIRST-seen provider (BFS order) wins when an id is (unusually) granted by more than
 * one signed-in provider; an id with no provider-sourced occurrence anywhere in the
 * tree is simply absent from the map.
 */
export function collectProviderCatalogIds(root: CatalogNode): Map<string, string> {
  const result = new Map<string, string>();
  const queue: Array<{ node: CatalogNode; providerName: string | undefined }> = root.children.map((c) => ({
    node: c,
    providerName: isProviderSourced(c) ? (c.providerName ?? "") : undefined,
  }));
  while (queue.length > 0) {
    const { node, providerName } = queue.shift()!;
    if (providerName !== undefined && dedupEligible(node.id) && !result.has(node.id)) {
      result.set(node.id, providerName);
    }
    for (const child of node.children) {
      queue.push({
        node: child,
        providerName:
          providerName !== undefined
            ? providerName
            : isProviderSourced(child)
              ? (child.providerName ?? "")
              : undefined,
      });
    }
  }
  return result;
}

/**
 * Why a row-backed catalog (a manual URL/inline catalog the user added — see {@link
 * dedupDisableReason}) was silently pruned by dedup, if it was — docs/313-PROVIDER-
 * CATALOG-LONGPRESS-AND-DEDUP-REASON.md v0. Generalizes docs/291's already-built
 * "Also provided by {name}" Manage-Catalogs reason (the `kind: "provider"` case here
 * reproduces it exactly) to the other row-backed dedup-disable case: superseded by
 * ANOTHER manually-added/preset catalog of the same id, with no provider involved.
 */
export type DedupDisableReason =
  | { kind: "provider"; providerName: string }
  | { kind: "duplicate"; otherTitle: string };

/**
 * For a row-backed catalog (`row.catalogId` — a manual URL row's resolved canonical id
 * via `sourceUrl`, or an inline row's own id via `inlineId`), decide whether the FINAL
 * deduped `tree` supersedes it, and why — docs/313 v0, generalizing docs/291's built
 * provider-vs-URL flag. Returns `null` when this row's own node is the one that survives
 * (it wasn't pruned) or the id has no primary occurrence anywhere (nothing to compare
 * against — a load failure, or an id genuinely absent from the tree).
 *
 * **The honest constraint this satisfies:** a row may be flagged "disabled" only when its
 * node was *actually* pruned from the final tree — "id ∈ some other primary occurrence"
 * isn't enough on its own, since docs/291 §Open-Q2 already found that flagging on a bare
 * id collision would be misleading for e.g. an inline catalog that's genuinely still
 * showing (its own primary occurrence just happens to share an id nested elsewhere). This
 * reuses {@link primaryKeepers} — the exact same primary-occurrence-only BFS
 * `dedupTreeById` prunes with — rather than a blind `findNodeById` walk, which is what
 * keeps the check honest for BOTH cases (see `primaryKeepers`'s own doc comment).
 */
export function dedupDisableReason(
  tree: CatalogNode,
  row: { catalogId: string; sourceUrl?: string; inlineId?: string },
): DedupDisableReason | null {
  const keeper = primaryKeepers(tree).get(row.catalogId);
  if (!keeper) return null;
  const isThisRow =
    (row.sourceUrl !== undefined &&
      keeper.node.removableSource?.kind === "url" &&
      keeper.node.removableSource.url === row.sourceUrl) ||
    (row.inlineId !== undefined &&
      keeper.node.removableSource?.kind === "inline" &&
      keeper.node.removableSource.id === row.inlineId);
  if (isThisRow) return null;
  if (keeper.provider) {
    const providerName = findRootNode(tree, keeper.node)?.providerName ?? "";
    return { kind: "provider", providerName };
  }
  return { kind: "duplicate", otherTitle: keeper.node.title };
}

/**
 * Splice one already-built root `node` into `tree`'s top-level children — replacing any
 * existing root with the same id, appending otherwise (order of the other roots is
 * untouched). Used by the Imports page's "open now" action
 * (docs/220-IMPORT-PROGRESS-AND-IMPORTS-PAGE.md v0) to make a just-imported inline
 * catalog reachable via the normal `/catalog-node?path=` route the instant an import
 * finishes, without waiting for (or triggering) a full remote-root tree rebuild.
 * Deliberately non-persistent — the caller decides whether/how to persist the result
 * (v0 never does, only mutates the in-session singleton); a future durable "splice on
 * every import" (docs/220 v1's `upsertInlineRoot`) can reuse this same primitive.
 */
export function spliceRootNode(tree: CatalogNode, node: CatalogNode): CatalogNode {
  return { ...tree, children: [...tree.children.filter((c) => c.id !== node.id), node] };
}

/** Find a catalog node by its slash path (empty path = the virtual root). */
export function findNode(root: CatalogNode, path: string): CatalogNode | null {
  if (!path) return root;
  let node: CatalogNode | undefined = root;
  for (const id of path.split("/")) {
    node = node?.children.find((c) => c.id === id);
    if (!node) return null;
  }
  return node ?? null;
}

/**
 * Find a catalog node by its **canonical** `id`, depth-first, returning the first match.
 * A cross-listed catalog (one `id`, multiple placements) resolves to whichever placement
 * is walked first — acceptable since it's the same content (docs/055-STORED-REFS-
 * CANONICAL-IDS.md); use {@link findNodeByPlacement} when the exact appearance matters.
 */
export function findNodeById(root: CatalogNode, catalogId: string): CatalogNode | null {
  if (root.id === catalogId) return root;
  for (const child of root.children) {
    const found = findNodeById(child, catalogId);
    if (found) return found;
  }
  return null;
}

/**
 * The top-level **root** ancestor of `node` — the first path segment resolved back to its
 * `tree.children` entry — or `node` itself when it already is a root. Only a root carries
 * `removableSource`/`originUrl` (never a drilled-down child, "never appears below the root
 * level"), so this is how a screen showing a non-root node resolves the ONE root whose
 * per-root signals (e.g. docs/257-REMOVED-ITEM-MARKERS.md v1's per-root "removed" scoping)
 * actually apply to it. Returns `null` for the synthetic virtual root itself (empty `path`,
 * no ancestor) or when `node`'s root segment isn't found in `tree` (stale/off-tree node).
 */
export function findRootNode(tree: CatalogNode, node: CatalogNode): CatalogNode | null {
  const rootId = node.path.split("/")[0];
  if (!rootId) return null;
  return tree.children.find((c) => c.id === rootId) ?? null;
}

/**
 * Find the catalog node whose own `packs` list contains a pack by canonical `id`,
 * depth-first, first match — i.e. the pack's **containing catalog**, used to
 * reconstruct a lesson/pack's ancestor breadcrumb (docs/097-CONTENT-BREADCRUMB.md).
 * A cross-listed pack (one `id`, multiple placements) resolves to whichever
 * placement is walked first, same caveat as {@link findNodeById}; precision via
 * `placementId` is a v1 follow-up (see the doc's phasing).
 */
export function findNodeContainingPack(root: CatalogNode, packId: string): CatalogNode | null {
  if (root.packs.some((p) => p.id === packId)) return root;
  for (const child of root.children) {
    const found = findNodeContainingPack(child, packId);
    if (found) return found;
  }
  return null;
}

/**
 * Find the catalog node whose own `packs` list contains an entry with this exact
 * `manifestUrl`, depth-first, first match — the manifestUrl-keyed counterpart to
 * {@link findNodeContainingPack}, needed for a Bible recent (docs/115-RECENTS-BIBLE-AND-
 * FILTERS.md, docs/120-RECENTS-LONG-PRESS-ACTIONS.md "Go to catalog") whose identity
 * (`bibleId`, a `translation:language` chokepoint) has no relation to a catalog pack
 * entry's own `id` — a Bible's true catalog identity is its `bible.json` manifestUrl
 * (the same join key `collectReachablePackUrls`/`collectBibles` use). Same cross-listing
 * caveat as `findNodeContainingPack`: a manifestUrl cross-listed under more than one node
 * resolves to whichever is walked first.
 */
export function findNodeContainingManifestUrl(root: CatalogNode, manifestUrl: string): CatalogNode | null {
  if (root.packs.some((p) => p.manifestUrl === manifestUrl)) return root;
  for (const child of root.children) {
    const found = findNodeContainingManifestUrl(child, manifestUrl);
    if (found) return found;
  }
  return null;
}

/**
 * Find a catalog node by its exact **`placementId`** (materials plan 022), depth-first.
 * Unlike {@link findNodeById}, this disambiguates a cross-listed catalog to the specific
 * appearance it was favorited/opened from.
 */
export function findNodeByPlacement(root: CatalogNode, placementId: string): CatalogNode | null {
  if (root.placementId === placementId) return root;
  for (const child of root.children) {
    const found = findNodeByPlacement(child, placementId);
    if (found) return found;
  }
  return null;
}

/**
 * Resolve a stored catalog reference (e.g. a `SavedCatalog`) to its current node in the
 * tree. Order: exact `placementId` match → first `catalogId` match (cross-listing
 * ambiguity — any placement is the same content) → the stored `path` (legacy fallback,
 * pre-`catalogId` entries). Returns `null` when nothing resolves. See
 * docs/055-STORED-REFS-CANONICAL-IDS.md.
 */
export function resolveSavedCatalogNode(
  tree: CatalogNode,
  ref: { catalogId?: string; placementId?: string; path?: string },
): CatalogNode | null {
  if (ref.placementId) {
    const byPlacement = findNodeByPlacement(tree, ref.placementId);
    if (byPlacement) return byPlacement;
  }
  if (ref.catalogId) {
    const byId = findNodeById(tree, ref.catalogId);
    if (byId) return byId;
  }
  if (ref.path !== undefined) return findNode(tree, ref.path);
  return null;
}

/**
 * Find a **root** catalog node whose `originUrl` OR `removableSource` URL normalizes to the
 * same catalog as `url` — the "is this permalink's catalog already added?" lookup for
 * docs/163-PERMALINKS-AND-DEEP-LINKS.md v0's add-or-view flow, broadened by docs/240-
 * PERMALINK-ADD-OR-VIEW-UX.md Half A to match **any** URL-sourced root (curated preset/
 * starter, provider), not just a user-`catalogStore`-added one — the original
 * `removableSource`-only check left a preset/starter catalog (which the loader never tags
 * `removableSource`) permanently unmatchable, misclassifying an already-present catalog as
 * "not added." Root-only (mirrors `removableSource`'s own "present only on a root node"
 * contract, see `CatalogNode.removableSource`) — a permalink never needs to match a
 * sub-catalog by URL.
 */
export function findNodeByCatalogUrl(root: CatalogNode, url: string): CatalogNode | null {
  const norm = normalizeCatalogUrl(url);
  return (
    root.children.find((child) => {
      if (child.originUrl && normalizeCatalogUrl(child.originUrl) === norm) return true;
      return child.removableSource?.kind === "url" && normalizeCatalogUrl(child.removableSource.url) === norm;
    }) ?? null
  );
}

/** Total packs reachable under a node (used for a count badge). */
export function countPacks(node: CatalogNode): number {
  return node.packs.length + node.children.reduce((n, c) => n + countPacks(c), 0);
}

/**
 * Every pack `id` reachable under a node (its own `packs` plus every descendant's),
 * recursively. Feeds the v1 "is this whole catalog complete?" check (docs/116-COMPLETED-
 * AND-ACCOMPLISHMENTS.md) via `@equipped/core/storage/progressIndex` `catalogComplete`.
 */
export function packIdsUnderNode(node: CatalogNode): string[] {
  const ids = node.packs.map((p) => p.id);
  for (const child of node.children) ids.push(...packIdsUnderNode(child));
  return ids;
}

/**
 * Every ancestor catalog node (innermost first, ending at the outermost root group) whose
 * subtree contains `packId` — the virtual root (empty `id`/`path`) is excluded since it
 * isn't a real, nameable catalog. Used to check "did completing this pack just finish one
 * or more containing catalogs?" (docs/116-COMPLETED-AND-ACCOMPLISHMENTS.md v1) without
 * re-walking the tree once per ancestor.
 */
export function ancestorNodesContainingPack(root: CatalogNode, packId: string): CatalogNode[] {
  const chain: CatalogNode[] = [];
  function walk(node: CatalogNode): boolean {
    const directHit = node.packs.some((p) => p.id === packId);
    const childHit = node.children.reduce((hit, c) => walk(c) || hit, false);
    if (directHit || childHit) {
      if (node.id !== "") chain.push(node);
      return true;
    }
    return false;
  }
  walk(root);
  return chain;
}

/**
 * Languages advertised across a node's own packs (not recursive into children) —
 * the union of each `CatalogPackEntry.languages`, deduped. Feeds the nav-bar
 * translate control's candidate list (intersected with shipped languages by
 * `@equipped/core/core/i18n` `availableLanguages`). See docs/035-PER-CONTENT-LANGUAGE.md.
 */
export function nodeLanguages(node: CatalogNode): string[] {
  const set = new Set<string>();
  for (const pack of node.packs) {
    for (const lang of pack.languages ?? []) set.add(lang);
  }
  return [...set];
}

/**
 * Languages advertised across EVERY pack anywhere under a node (recursive), deduped.
 * Used by the top-level Catalogs tab's translate control, whose picker should offer
 * everything reachable from the tree root, not just packs at the root level.
 */
export function treeLanguages(node: CatalogNode): string[] {
  const set = new Set<string>();
  const collect = (n: CatalogNode) => {
    for (const pack of n.packs) for (const lang of pack.languages ?? []) set.add(lang);
    n.children.forEach(collect);
  };
  collect(node);
  return [...set];
}
